import {clsx} from 'clsx'
import {AlertIcon, DeviceDesktopIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import styles from './SessionContinuationBanner.module.css'

export type SessionContinuationBannerVariant = 'continue' | 'offline'

interface SessionContinuationBannerProps {
  variant: SessionContinuationBannerVariant
  /** URL for starting a separate cloud session. The button is hidden when omitted. */
  newConversationHref?: string
  /** Renders as a header attached to the top of the composer instead of replacing it. */
  attached?: boolean
}

const TITLES: Record<SessionContinuationBannerVariant, string> = {
  continue: 'Continue this session where it was started',
  offline: 'This session is offline',
}

const BODIES: Record<SessionContinuationBannerVariant, {withCloud: string; withoutCloud: string}> = {
  continue: {
    withCloud: 'You can view updates here. Start a new session in the cloud to work from the web.',
    withoutCloud: 'You can view updates here.',
  },
  offline: {
    withCloud: 'Open this session where it was started to continue, or start a new session in the cloud.',
    withoutCloud: 'Open this session where it was started to continue.',
  },
}

export function SessionContinuationBanner({variant, newConversationHref, attached}: SessionContinuationBannerProps) {
  const Icon = variant === 'offline' ? AlertIcon : DeviceDesktopIcon
  return (
    <div className={clsx(styles.banner, attached && styles.attached)}>
      {/* Focusable by script only, never in the tab order. When this banner replaces
          the composer it has to be able to receive focus when a neighbouring
          control unmounts and would otherwise drop focus to the document. */}
      <div className={styles.content} data-testid="session-continuation-banner" tabIndex={-1}>
        <p className={styles.title}>
          <Icon size={16} className={variant === 'offline' ? styles.offlineIcon : styles.icon} />
          <strong>{TITLES[variant]}</strong>
        </p>
        <p className={styles.body}>{newConversationHref ? BODIES[variant].withCloud : BODIES[variant].withoutCloud}</p>
      </div>
      {newConversationHref ? (
        <Button as="a" href={newConversationHref} variant="primary" className={styles.action}>
          New cloud session
        </Button>
      ) : null}
    </div>
  )
}
